import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Upload, Users, Shield, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExcelPreviewDialog from "../../components/ExcelPreviewDialog";

export default function FilerTab({ token, eventId, eventName, onCountChange, showConfirm, closeConfirm }) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewDialog, setPreviewDialog] = useState({ open: false, file: null, fileType: null });

  const loadUploadedFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/uploaded-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setUploadedFiles(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error("Fejl ved hentning af filer", error);
    }
  }, [eventId, token, onCountChange]);

  useEffect(() => { loadUploadedFiles(); }, [loadUploadedFiles]);

  const handleSeparateUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreviewDialog({ open: true, file, fileType: type });
    e.target.value = '';
  };

  const handlePreviewImportComplete = () => {
    loadUploadedFiles();
  };

  const handleDeleteImport = (type) => {
    const typeName = type === 'volunteers' ? 'frivillige' : 'teams';
    showConfirm(
      `Slet importerede ${typeName}?`,
      `Er du sikker på du vil slette alle importerede ${typeName}? Manuelt tilføjede ${typeName} bevares.`,
      async () => {
        closeConfirm();
        try {
          const endpoint = type === 'volunteers'
            ? `${API}/events/${eventId}/imported-volunteers`
            : `${API}/events/${eventId}/imported-roles`;
          await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` }});
          toast.success(`Importerede ${typeName} slettet`);
          loadUploadedFiles();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Uploadede Filer</h2>
          <p className="text-sm text-muted-foreground">Upload separate Excel-filer for frivillige og teams til {eventName}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Frivillige Upload */}
        <div className="p-6 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-green-500/10"><Users className="h-6 w-6 text-green-500" /></div>
            <div><h3 className="font-bold text-lg">Frivillige</h3><p className="text-sm text-muted-foreground">Upload Excel med frivillige</p></div>
          </div>
          <input type="file" accept=".xlsx,.xls" id="volunteers-upload" onChange={(e) => handleSeparateUpload(e, 'volunteers')} className="hidden" data-testid="volunteers-file-input" />
          {uploadedFiles.find(f => f.type === 'imported_volunteers') ? (
            <div className="space-y-3">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-400">{uploadedFiles.find(f => f.type === 'imported_volunteers')?.filename}</p>
                    <p className="text-sm text-muted-foreground">{uploadedFiles.find(f => f.type === 'imported_volunteers')?.count} frivillige</p>
                    {uploadedFiles.find(f => f.type === 'imported_volunteers')?.uploaded_at && (
                      <p className="text-xs text-muted-foreground mt-1">Uploadet: {new Date(uploadedFiles.find(f => f.type === 'imported_volunteers')?.uploaded_at).toLocaleString('da-DK')}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteImport('volunteers')} data-testid="delete-volunteers-btn"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => document.getElementById('volunteers-upload').click()}>
                <Upload className="h-4 w-4 mr-2" />Tilføj flere frivillige (gengangere springes over)
              </Button>
            </div>
          ) : (
            <Button className="w-full" onClick={() => document.getElementById('volunteers-upload').click()} data-testid="upload-volunteers-btn">
              <Eye className="h-4 w-4 mr-2" />Upload frivillige (med forhåndsvisning)
            </Button>
          )}
          <p className="text-xs text-muted-foreground mt-3">Upload flere filer efter hinanden • Gengangere genkendes automatisk på email</p>
        </div>

        {/* Teams Upload */}
        <div className="p-6 bg-card border border-border rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-blue-500/10"><Shield className="h-6 w-6 text-blue-500" /></div>
            <div><h3 className="font-bold text-lg">Teams</h3><p className="text-sm text-muted-foreground">Upload Excel med teams</p></div>
          </div>
          <input type="file" accept=".xlsx,.xls" id="roles-upload" onChange={(e) => handleSeparateUpload(e, 'roles')} className="hidden" data-testid="roles-file-input" />
          {uploadedFiles.find(f => f.type === 'imported_roles') ? (
            <div className="space-y-3">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-400">{uploadedFiles.find(f => f.type === 'imported_roles')?.filename}</p>
                    <p className="text-sm text-muted-foreground">{uploadedFiles.find(f => f.type === 'imported_roles')?.count} teams</p>
                    {uploadedFiles.find(f => f.type === 'imported_roles')?.uploaded_at && (
                      <p className="text-xs text-muted-foreground mt-1">Uploadet: {new Date(uploadedFiles.find(f => f.type === 'imported_roles')?.uploaded_at).toLocaleString('da-DK')}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteImport('roles')} data-testid="delete-roles-btn"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => document.getElementById('roles-upload').click()}>
                <Upload className="h-4 w-4 mr-2" />Erstat fil (med forhåndsvisning)
              </Button>
            </div>
          ) : (
            <Button className="w-full" onClick={() => document.getElementById('roles-upload').click()} data-testid="upload-roles-btn">
              <Eye className="h-4 w-4 mr-2" />Upload teams (med forhåndsvisning)
            </Button>
          )}
          <p className="text-xs text-muted-foreground mt-3">Vælg kolonner før import • Kræver rollenavne</p>
        </div>
      </div>

      {/* Excel Preview Dialog */}
      {previewDialog.open && (
        <ExcelPreviewDialog
          open={previewDialog.open}
          onClose={() => setPreviewDialog({ open: false, file: null, fileType: null })}
          file={previewDialog.file}
          fileType={previewDialog.fileType}
          eventId={eventId}
          token={token}
          onImportComplete={handlePreviewImportComplete}
        />
      )}
    </div>
  );
}
